import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { createRingtoneRinger, getStoredRingtoneChoice, setStoredRingtoneChoice, getCustomRingtoneUrl, loadCustomRingtoneUrl } from '../utils/callRingtones';

const CallContext = createContext(null);
export const useCall = () => useContext(CallContext);

const VIDEO_CONSTRAINTS = { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' };
// Explicit processing flags normalise the SENT voice level (autoGainControl
// lifts quiet mics — the peer hears you louder and more evenly).
const AUDIO_CONSTRAINTS = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
  || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

// Simple WebAudio ring tones (asset-free, native apps will use system ringtones)
export const createRinger = (pattern) => {
  let ctx = null;
  let interval = null;
  const beep = () => {
    try {
      if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = pattern.freq;
      const peak = pattern.gain || 0.18;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(peak, ctx.currentTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + pattern.beep);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + pattern.beep + 0.05);
    } catch (e) { /* autoplay blocked — silent */ }
  };
  const vibe = () => {
    if (pattern.vibrate && navigator.vibrate) {
      try { navigator.vibrate([350, 200, 350]); } catch (e) {}
    }
  };
  return {
    start: () => { if (interval) return; beep(); vibe(); interval = setInterval(() => { beep(); vibe(); }, pattern.every); },
    stop: () => {
      if (interval) { clearInterval(interval); interval = null; }
      if (pattern.vibrate && navigator.vibrate) { try { navigator.vibrate(0); } catch (e) {} }
    },
  };
};
// (incoming ring pattern now lives in utils/callRingtones.js — user-selectable)

export const CallProvider = ({ children }) => {
  const { api, user, onDeviceUpdate, isAuthenticated } = useAuth();
  const [call, setCall] = useState(null); // {call_id, conversation_id, peer_name, media, role, status}
  const [muted, setMuted] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const [remoteHasVideo, setRemoteHasVideo] = useState(false);
  const [peerPaused, setPeerPaused] = useState(null); // null | 'away' (peer signalled) | 'network' (media stalled)
  const [volumeBoost, setVolumeBoost] = useState(() => localStorage.getItem('call_volume_boost') !== 'off');
  const volumeBoostRef = useRef(volumeBoost);
  useEffect(() => { volumeBoostRef.current = volumeBoost; }, [volumeBoost]);
  const [callSeconds, setCallSeconds] = useState(0);
  const [callChatToast, setCallChatToast] = useState(null); // {id, sender, text}
  const [callChatUnread, setCallChatUnread] = useState(0);
  const chatToastTimerRef = useRef(null);

  const callRef = useRef(null);
  const cameraOnRef = useRef(false);
  const screenSharingRef = useRef(false);
  const screenTrackRef = useRef(null);
  const cameraWasOnRef = useRef(false);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const remoteAudioElRef = useRef(null);
  const pendingIceRef = useRef([]);
  const performIceRestartRef = useRef(null);
  const offerRetryRef = useRef(null);
  const timerRef = useRef(null);
  const incomingRingRef = useRef(null);
  const ringbackRef = useRef(null);
  const audioBoostRef = useRef(null);
  const answeringRef = useRef(false);
  const disconnectTimerRef = useRef(null);
  const seenSeqRef = useRef(new Set());
  const pollCursorRef = useRef(0);
  const offerStartingRef = useRef(false);

  useEffect(() => { callRef.current = call; }, [call]);
  useEffect(() => { cameraOnRef.current = cameraOn; }, [cameraOn]);
  useEffect(() => { screenSharingRef.current = screenSharing; }, [screenSharing]);

  const stopRingers = () => {
    incomingRingRef.current?.stop();
    ringbackRef.current?.stop();
  };

  // Start the incoming-call ring using the user's chosen ringtone (built-in or custom upload).
  const startIncomingRing = useCallback(() => {
    incomingRingRef.current?.stop();
    incomingRingRef.current = createRingtoneRinger(getStoredRingtoneChoice(), getCustomRingtoneUrl());
    incomingRingRef.current.start();
  }, []);

  // Sync the server-side ringtone choice into localStorage and preload the custom
  // audio blob so it is ready the instant a call arrives.
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    (async () => {
      try {
        const { data } = await api.get('/settings/notifications');
        const choice = data?.call_ringtone || 'classic';
        setStoredRingtoneChoice(choice);
        if (choice === 'custom') await loadCustomRingtoneUrl(api);
      } catch (e) { /* keep localStorage value */ }
    })();
  }, [isAuthenticated, user?.id, api]);

  // Speaker loudness boost (mobile speakers are quiet): route remote audio
  // through a GainNode + soft limiter. Skipped on iOS where WebAudio playback
  // of remote WebRTC streams is unreliable — the <audio> element stays primary.
  // NOTE: the element keeps srcObject even when muted (Chrome requires a media
  // element sink for remote streams to produce WebAudio output).
  const detachAudioBoost = () => {
    const b = audioBoostRef.current;
    if (b) { try { b.ctx.close(); } catch (e) {} audioBoostRef.current = null; }
    if (remoteAudioElRef.current) remoteAudioElRef.current.muted = false;
  };
  const attachAudioBoost = (stream) => {
    if (IS_IOS) return;
    try {
      if (audioBoostRef.current?.stream === stream) return;
      detachAudioBoost();
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const source = ctx.createMediaStreamSource(stream);
      const gain = ctx.createGain();
      gain.gain.value = volumeBoostRef.current ? 2.5 : 1.0;
      const limiter = ctx.createDynamicsCompressor();
      limiter.threshold.value = -6;
      limiter.ratio.value = 12;
      source.connect(gain).connect(limiter).connect(ctx.destination);
      audioBoostRef.current = { ctx, stream, gain };
      if (remoteAudioElRef.current) remoteAudioElRef.current.muted = true; // avoid double audio
    } catch (e) { /* element playback remains the fallback */ }
  };

  const cleanup = useCallback(() => {
    stopRingers();
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (offerRetryRef.current) { clearInterval(offerRetryRef.current); offerRetryRef.current = null; }
    if (disconnectTimerRef.current) { clearTimeout(disconnectTimerRef.current); disconnectTimerRef.current = null; }
    detachAudioBoost();
    answeringRef.current = false;
    offerStartingRef.current = false;
    seenSeqRef.current = new Set();
    pollCursorRef.current = 0;
    if (pcRef.current) { try { pcRef.current.close(); } catch (e) {} pcRef.current = null; }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    if (remoteAudioElRef.current) {
      try { remoteAudioElRef.current.srcObject = null; } catch (e) {}
    }
    remoteStreamRef.current = null;
    pendingIceRef.current = [];
    screenTrackRef.current = null;
    cameraWasOnRef.current = false;
    setMuted(false);
    setCameraOn(false);
    setScreenSharing(false);
    setRemoteHasVideo(false);
    setPeerPaused(null);
    setCallSeconds(0);
    setCallChatToast(null);
    setCallChatUnread(0);
    if (chatToastTimerRef.current) { clearTimeout(chatToastTimerRef.current); chatToastTimerRef.current = null; }
    setCall(null);
  }, []);

  const startTimer = () => {
    if (timerRef.current) return;
    timerRef.current = setInterval(() => setCallSeconds(s => s + 1), 1000);
  };

  const sendSignal = useCallback(async (callId, kind, payload) => {
    try {
      await api.post(`/calls/${callId}/signal`, { kind, payload });
    } catch (e) {
      if (e.response?.status !== 409) console.error('Signal failed:', e);
    }
  }, [api]);

  const refreshRemoteVideoState = useCallback(() => {
    const stream = remoteStreamRef.current;
    const live = !!stream && stream.getVideoTracks().some(t => t.readyState === 'live' && !t.muted);
    setRemoteHasVideo(live);
  }, []);

  const buildPeerConnection = useCallback(async (callId) => {
    const { data } = await api.get('/webrtc/ice-config');
    const pc = new RTCPeerConnection({ iceServers: data.iceServers });
    pcRef.current = pc;

    localStreamRef.current?.getTracks().forEach(track => pc.addTrack(track, localStreamRef.current));

    pc.onicecandidate = (e) => {
      if (e.candidate) sendSignal(callId, 'ice', e.candidate.toJSON());
    };
    pc.ontrack = (e) => {
      const stream = e.streams[0];
      remoteStreamRef.current = stream;
      // DOM-attached <audio playsInline> — required for reliable iOS Safari playback
      const el = remoteAudioElRef.current;
      if (el && el.srcObject !== stream) {
        el.srcObject = stream;
        el.volume = 1.0;
        el.play().catch(() => {});
      }
      if (e.track.kind === 'audio') attachAudioBoost(stream);
      if (e.track.kind === 'video') {
        e.track.onmute = refreshRemoteVideoState;
        e.track.onunmute = refreshRemoteVideoState;
        e.track.onended = refreshRemoteVideoState;
      }
      if (e.track.kind === 'audio') {
        // Autonomous stall detection — fires when the peer's RTP stops (their OS
        // suspended the browser for a native call) even if they couldn't signal us.
        e.track.onmute = () => setPeerPaused(p => p ?? 'network');
        e.track.onunmute = () => setPeerPaused(p => (p === 'network' ? null : p));
      }
      stream.onremovetrack = refreshRemoteVideoState;
      refreshRemoteVideoState();
    };
    const markConnected = () => {
      stopRingers();
      setCall(prev => (prev && prev.status !== 'active' ? { ...prev, status: 'active' } : prev));
      startTimer();
    };
    pc.onconnectionstatechange = () => {
      if (!pcRef.current) return;
      const state = pcRef.current.connectionState;
      if (state === 'connected') {
        markConnected();
      } else if (state === 'failed') {
        // Caller drives the restart; the answer exchange restarts the callee's ICE too
        if (callRef.current?.role === 'caller') performIceRestartRef.current?.();
      }
    };
    // Older iOS Safari doesn't reliably fire connectionstatechange — fall back to ICE state.
    // 'disconnected' often self-heals; if it persists >4s the caller restarts ICE.
    pc.oniceconnectionstatechange = () => {
      if (!pcRef.current) return;
      const ice = pcRef.current.iceConnectionState;
      if (ice === 'connected' || ice === 'completed') {
        if (disconnectTimerRef.current) { clearTimeout(disconnectTimerRef.current); disconnectTimerRef.current = null; }
        markConnected();
      } else if (ice === 'disconnected' && !disconnectTimerRef.current) {
        disconnectTimerRef.current = setTimeout(() => {
          disconnectTimerRef.current = null;
          const live = pcRef.current;
          if (live && ['disconnected', 'failed'].includes(live.iceConnectionState) && callRef.current?.role === 'caller') {
            performIceRestartRef.current?.();
          }
        }, 4000);
      }
    };
    return pc;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, sendSignal, refreshRemoteVideoState]);

  // Get mic (and camera for video calls). Degrades to audio-only if the camera fails.
  const getMedia = async (wantVideo) => {
    if (wantVideo) {
      try {
        localStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS, video: VIDEO_CONSTRAINTS });
        setCameraOn(true);
        return true;
      } catch (e) {
        toast.info('Camera unavailable — continuing with audio only');
      }
    }
    try {
      localStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
      setCameraOn(false);
      return true;
    } catch (e) {
      if (e?.name !== 'NotAllowedError') {
        // Some Android hardware rejects processing constraints — retry plain audio
        try {
          localStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
          setCameraOn(false);
          return true;
        } catch (e2) { /* fall through */ }
      }
      toast.error('Microphone access is required for calls');
      return false;
    }
  };

  const drainPendingIce = async (pc) => {
    for (const c of pendingIceRef.current) {
      try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) {}
    }
    pendingIceRef.current = [];
  };

  // Re-offer after adding/removing tracks mid-call (camera toggle)
  const renegotiate = useCallback(async () => {
    const current = callRef.current;
    const pc = pcRef.current;
    if (!current || !pc) return;
    try {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await sendSignal(current.call_id, 'sdp-offer', { sdp: offer.sdp, type: offer.type });
    } catch (e) {
      console.error('Renegotiation failed:', e);
    }
  }, [sendSignal]);

  // Caller-driven ICE restart — regenerates candidates on BOTH sides via offer/answer.
  // (pc.restartIce() alone only flags the next offer; without sending one it is a no-op.)
  const performIceRestart = useCallback(async () => {
    const current = callRef.current;
    const pc = pcRef.current;
    if (!current || !pc || current.role !== 'caller') return;
    try {
      const offer = await pc.createOffer({ iceRestart: true });
      await pc.setLocalDescription(offer);
      await sendSignal(current.call_id, 'sdp-offer', { sdp: offer.sdp, type: offer.type });
    } catch (e) {
      console.error('ICE restart failed:', e);
    }
  }, [sendSignal]);
  useEffect(() => { performIceRestartRef.current = performIceRestart; }, [performIceRestart]);

  // ── Actions ──
  const startCall = useCallback(async (conversation, peerName, media = 'voice') => {
    if (callRef.current) { toast.error('You are already on a call'); return; }
    if (!(await getMedia(media === 'video'))) return;
    try {
      const { data } = await api.post('/calls/initiate', { conversation_id: conversation.id, media });
      seenSeqRef.current = new Set();
      pollCursorRef.current = 0;
      offerStartingRef.current = false;
      setCall({
        call_id: data.call_id,
        conversation_id: conversation.id,
        peer_name: peerName || data.callee_name,
        media,
        role: 'caller',
        status: 'ringing',
      });
      stopRingers(); // ensure no stray incoming ringer overlaps the ringback
      if (!ringbackRef.current) ringbackRef.current = createRinger({ freq: 425, beep: 0.9, every: 3200 });
      ringbackRef.current.start();
    } catch (e) {
      localStreamRef.current?.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
      setCameraOn(false);
      toast.error(e.response?.data?.detail || 'Could not start the call');
    }
  }, [api]);

  const acceptCall = useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    stopRingers();
    answeringRef.current = true;
    if (!(await getMedia(current.media === 'video'))) {
      // Mic denied — decline so the caller hangs up immediately instead of
      // ringing until the 35s timeout.
      answeringRef.current = false;
      api.post(`/calls/${current.call_id}/decline`).catch(() => {});
      cleanup();
      return;
    }
    try {
      await api.post(`/calls/${current.call_id}/answer`);
      setCall(prev => (prev ? { ...prev, status: 'connecting' } : prev));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not answer the call');
      cleanup();
    }
  }, [api, cleanup]);

  const declineCall = useCallback(async () => {
    const current = callRef.current;
    if (!current) return;
    cleanup(); // before the POST — the server's call_ended echo must not double-toast
    try { await api.post(`/calls/${current.call_id}/decline`); } catch (e) {}
  }, [api, cleanup]);

  const endCall = useCallback(async (reason) => {
    const current = callRef.current;
    if (!current) return;
    const body = typeof reason === 'string' ? { reason } : {};
    cleanup(); // before the POST — the server's call_ended echo must not double-toast
    try { await api.post(`/calls/${current.call_id}/end`, body); } catch (e) {}
  }, [api, cleanup]);

  // Caller proceeds to the SDP offer once the callee answers. Idempotent —
  // callable from the WS event AND the reconciliation poller without double-building.
  const beginCallerOffer = useCallback(async () => {
    const current = callRef.current;
    if (!current || current.role !== 'caller' || pcRef.current || offerStartingRef.current) return;
    offerStartingRef.current = true;
    stopRingers();
    setCall(prev => (prev && prev.status === 'ringing' ? { ...prev, status: 'connecting' } : prev));
    try {
      const pc = await buildPeerConnection(current.call_id);
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await sendSignal(current.call_id, 'sdp-offer', { sdp: offer.sdp, type: offer.type });
      // Retry the initial offer — the callee's WS may still be reconnecting
      // after their device resumed from suspension, losing the broadcast.
      // Re-sends use localDescription, which accumulates gathered ICE.
      let attempts = 0;
      offerRetryRef.current = setInterval(() => {
        const live = pcRef.current;
        if (!live || live !== pc || live.signalingState !== 'have-local-offer' || attempts >= 3) {
          clearInterval(offerRetryRef.current);
          offerRetryRef.current = null;
          return;
        }
        attempts += 1;
        const ld = live.localDescription;
        if (ld) sendSignal(current.call_id, 'sdp-offer', { sdp: ld.sdp, type: ld.type });
      }, 4000);
    } catch (e) {
      console.error('Offer failed:', e);
      endCall();
    }
  }, [buildPeerConnection, sendSignal, endCall]);

  // Apply one SDP/ICE/hold signal. Shared by the WS handler and the REST
  // reconciliation poller — seq-based dedupe makes dual delivery safe.
  const processSignal = useCallback(async (data) => {
    const current = callRef.current;
    if (!current) return;
    if (typeof data.seq === 'number') {
      if (seenSeqRef.current.has(data.seq)) return;
      seenSeqRef.current.add(data.seq);
    }
    try {
      if (data.kind === 'hold') {
        setPeerPaused('away');
        return;
      }
      if (data.kind === 'resume') {
        setPeerPaused(null);
        return;
      }
      if (data.kind === 'video-off') {
        // Peer turned their camera off — go black instantly (don't wait for RTP mute)
        setRemoteHasVideo(false);
        return;
      }
      if (data.kind === 'video-on') {
        setRemoteHasVideo(true);
        return;
      }
      if (data.kind === 'sdp-offer') {
        if (pcRef.current) {
          // Mid-call renegotiation or ICE restart from the peer
          const pc = pcRef.current;
          if (pc.signalingState === 'have-local-offer') {
            if (current.role === 'caller') return; // glare: impolite caller ignores, peer answers ours
            await pc.setLocalDescription({ type: 'rollback' }); // polite callee rolls back
          }
          await pc.setRemoteDescription(new RTCSessionDescription(data.payload));
          await drainPendingIce(pc);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          await sendSignal(current.call_id, 'sdp-answer', { sdp: answer.sdp, type: answer.type });
          refreshRemoteVideoState();
        } else if (current.role === 'callee') {
          // Initial offer
          const pc = await buildPeerConnection(current.call_id);
          await pc.setRemoteDescription(new RTCSessionDescription(data.payload));
          await drainPendingIce(pc);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          await sendSignal(current.call_id, 'sdp-answer', { sdp: answer.sdp, type: answer.type });
        }
      } else if (data.kind === 'sdp-answer') {
        if (pcRef.current?.signalingState === 'have-local-offer') {
          await pcRef.current.setRemoteDescription(new RTCSessionDescription(data.payload));
          await drainPendingIce(pcRef.current);
          refreshRemoteVideoState();
        }
      } else if (data.kind === 'ice') {
        if (pcRef.current?.remoteDescription) {
          await pcRef.current.addIceCandidate(new RTCIceCandidate(data.payload));
        } else {
          pendingIceRef.current.push(data.payload);
        }
      }
    } catch (e) {
      console.error('Signal handling failed:', e);
    }
  }, [buildPeerConnection, sendSignal, refreshRemoteVideoState]);

  const toggleMute = useCallback(() => {
    const stream = localStreamRef.current;
    if (!stream) return;
    const next = !muted;
    stream.getAudioTracks().forEach(t => { t.enabled = !next; });
    setMuted(next);
  }, [muted]);

  // Loudness switch — Normal (1x) / Loud (2.5x) on the WebAudio boost path.
  // (Browsers can't route to the phone earpiece; that needs the native app.)
  const toggleVolumeBoost = useCallback(() => {
    setVolumeBoost((v) => {
      const next = !v;
      localStorage.setItem('call_volume_boost', next ? 'on' : 'off');
      const gainNode = audioBoostRef.current?.gain;
      if (gainNode) gainNode.gain.value = next ? 2.5 : 1.0;
      return next;
    });
  }, []);

  // ── Screen sharing ──
  const stopScreenShare = useCallback(async (revertToCamera) => {
    const pc = pcRef.current;
    const stream = localStreamRef.current;
    const track = screenTrackRef.current;
    if (!track) return;
    screenTrackRef.current = null;
    const sender = pc?.getSenders().find(s => s.track === track);
    track.onended = null;
    track.stop();
    stream?.removeTrack(track);
    if (revertToCamera) {
      try {
        const camStream = await navigator.mediaDevices.getUserMedia({ video: VIDEO_CONSTRAINTS });
        const camTrack = camStream.getVideoTracks()[0];
        stream?.addTrack(camTrack);
        if (sender) await sender.replaceTrack(camTrack);
        else if (pc) { pc.addTrack(camTrack, stream); await renegotiate(); }
        setScreenSharing(false);
        setCameraOn(true);
        if (callRef.current) sendSignal(callRef.current.call_id, 'video-on', {});
        return;
      } catch (e) { /* camera unavailable — fall through to video off */ }
    }
    if (pc && sender) { pc.removeTrack(sender); await renegotiate(); }
    setScreenSharing(false);
    setCameraOn(false);
    if (callRef.current) sendSignal(callRef.current.call_id, 'video-off', {});
  }, [renegotiate, sendSignal]);

  const toggleScreenShare = useCallback(async () => {
    const pc = pcRef.current;
    const stream = localStreamRef.current;
    if (!stream) return;
    if (screenSharingRef.current) {
      await stopScreenShare(cameraWasOnRef.current);
      return;
    }
    if (!navigator.mediaDevices?.getDisplayMedia) {
      toast.error('Screen sharing is not supported on this device');
      return;
    }
    try {
      const display = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const track = display.getVideoTracks()[0];
      cameraWasOnRef.current = cameraOnRef.current;
      const camTrack = stream.getVideoTracks()[0];
      const sender = pc?.getSenders().find(s => s.track && s.track.kind === 'video');
      if (camTrack) { camTrack.stop(); stream.removeTrack(camTrack); }
      stream.addTrack(track);
      if (sender) await sender.replaceTrack(track);
      else if (pc) { pc.addTrack(track, stream); await renegotiate(); }
      screenTrackRef.current = track;
      // Browser "Stop sharing" bar → revert gracefully
      track.onended = () => { stopScreenShare(cameraWasOnRef.current); };
      setScreenSharing(true);
      setCameraOn(false);
      if (callRef.current) sendSignal(callRef.current.call_id, 'video-on', {});
    } catch (e) {
      if (e?.name !== 'NotAllowedError') toast.error('Could not start screen sharing');
    }
  }, [stopScreenShare, renegotiate, sendSignal]);

  const toggleCamera = useCallback(async () => {
    const pc = pcRef.current;
    const stream = localStreamRef.current;
    if (!stream) return;
    if (screenSharingRef.current) {
      // Sharing → camera: swap the outgoing track back to the camera
      await stopScreenShare(true);
      return;
    }
    if (!cameraOnRef.current) {
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({ video: VIDEO_CONSTRAINTS });
        const track = videoStream.getVideoTracks()[0];
        stream.addTrack(track);
        if (pc) {
          pc.addTrack(track, stream);
          await renegotiate();
        }
        setCameraOn(true);
        if (callRef.current) sendSignal(callRef.current.call_id, 'video-on', {});
      } catch (e) {
        toast.error('Could not access the camera');
      }
    } else {
      const track = stream.getVideoTracks()[0];
      if (track) {
        const sender = pc?.getSenders().find(s => s.track === track);
        if (pc && sender) pc.removeTrack(sender);
        track.stop();
        stream.removeTrack(track);
        if (pc) await renegotiate();
      }
      setCameraOn(false);
      if (callRef.current) sendSignal(callRef.current.call_id, 'video-off', {});
    }
  }, [renegotiate, stopScreenShare, sendSignal]);

  // ── Front/back camera flip (mobile) ──
  const [cameraFacing, setCameraFacing] = useState('user');
  const cameraFacingRef = useRef('user');
  useEffect(() => { cameraFacingRef.current = cameraFacing; }, [cameraFacing]);
  const [multipleCameras, setMultipleCameras] = useState(false);
  const refreshCameraCount = useCallback(async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices();
      setMultipleCameras(devs.filter(d => d.kind === 'videoinput').length > 1);
    } catch (e) {}
  }, []);
  useEffect(() => {
    if (!call?.call_id) return undefined;
    refreshCameraCount();
    const h = () => refreshCameraCount();
    navigator.mediaDevices?.addEventListener?.('devicechange', h);
    return () => navigator.mediaDevices?.removeEventListener?.('devicechange', h);
  }, [call?.call_id, refreshCameraCount]);

  const flipCamera = useCallback(async () => {
    const pc = pcRef.current;
    const stream = localStreamRef.current;
    if (!stream || !cameraOnRef.current || screenSharingRef.current) return;
    const next = cameraFacingRef.current === 'user' ? 'environment' : 'user';
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { ...VIDEO_CONSTRAINTS, facingMode: { ideal: next } },
      });
      const newTrack = newStream.getVideoTracks()[0];
      const oldTrack = stream.getVideoTracks()[0];
      const sender = pc?.getSenders().find(s => s.track && s.track.kind === 'video');
      if (sender) await sender.replaceTrack(newTrack);
      if (oldTrack) { oldTrack.stop(); stream.removeTrack(oldTrack); }
      stream.addTrack(newTrack);
      // Only un-mirror if the camera really switched to the back one
      const actual = newTrack.getSettings?.().facingMode;
      setCameraFacing(actual === 'environment' ? 'environment' : 'user');
    } catch (e) {
      toast.error('Could not switch camera');
    }
  }, []);

  // ── WS event handling ──
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const unsubscribe = onDeviceUpdate(async (_deviceId, data) => {
      if (!data?.type) return;
      // Identity guard — never act on call events addressed to another account
      // (protects against a stale tab/session receiving someone else's events).
      if (data.type.startsWith('call_') && Array.isArray(data.user_ids) && user?.id && !data.user_ids.includes(user.id)) return;
      const current = callRef.current;

      // In-call chat toast: surface messages for THIS call's conversation
      if (data.type === 'chat_message') {
        const msg = data.message;
        if (Array.isArray(data.user_ids) && user?.id && !data.user_ids.includes(user.id)) return;
        if (current && current.status === 'active' && msg && data.conversation_id === current.conversation_id
            && msg.sender_id !== user?.id && msg.type !== 'call' && !msg.deleted) {
          setCallChatUnread(n => n + 1);
          setCallChatToast({ id: msg.id, sender: msg.sender_name, text: msg.content || 'Sent an attachment' });
          if (chatToastTimerRef.current) clearTimeout(chatToastTimerRef.current);
          chatToastTimerRef.current = setTimeout(() => setCallChatToast(null), 4500);
        }
        return;
      }

      if (data.type === 'call_invite') {
        if (data.caller_id && user?.id && data.caller_id === user.id) return; // own outgoing call echoed back
        if (current) return; // busy — server logs missed via timeout/decline
        setCall({
          call_id: data.call_id,
          conversation_id: data.conversation_id,
          peer_name: data.caller_name,
          media: data.media || 'voice',
          role: 'callee',
          status: 'ringing',
          incoming_expires_at: Date.now() + 38000,
        });
        // Mute Calls Too: muted conversations show the incoming call silently
        let convMuted = false;
        try { convMuted = (JSON.parse(localStorage.getItem('muted_conversation_ids') || '[]')).includes(data.conversation_id); } catch (e) {}
        if (!convMuted) startIncomingRing();
        return;
      }

      if (!current || data.call_id !== current.call_id) return;

      // My other tab/device answered this call — stop ringing here silently
      if (data.type === 'call_accepted' && current.role === 'callee' && current.status === 'ringing' && !answeringRef.current) {
        cleanup();
        return;
      }

      if (data.type === 'call_accepted' && current.role === 'caller') {
        beginCallerOffer();
        return;
      }

      if (data.type === 'call_signal') {
        await processSignal(data);
        return;
      }

      if (data.type === 'call_ended' || data.type === 'call_declined') {
        const reason = data.reason;
        if (reason === 'declined' && current.role === 'caller') toast.info(`${current.peer_name} declined the call`);
        else if (reason === 'timeout') toast.info('Call not answered');
        else if (reason === 'cancelled' && current.role === 'callee') toast.info('Call cancelled');
        else if (reason === 'connect_timeout') toast.error("Couldn't connect the call — this network may require the call relay server");
        cleanup();
      }
    });
    return unsubscribe;
  }, [isAuthenticated, onDeviceUpdate, user?.id, beginCallerOffer, processSignal, cleanup, startIncomingRing]);

  // Recover ringing state — on load AND every app resume (suspended iPhone/Android
  // PWAs miss the one-time call_invite WS event; this re-checks the server).
  const recoverActiveCall = useCallback(async () => {
    if (!isAuthenticated || !user?.id || callRef.current) return;
    try {
      const { data } = await api.get('/calls/active');
      const active = data.call;
      if (active && active.status === 'ringing' && active.callee_id === user.id && !callRef.current) {
        let expiresAt = Date.now() + 38000;
        if (active.created_at) {
          const age = (Date.now() - new Date(active.created_at).getTime()) / 1000;
          if (Number.isFinite(age) && age > 0) expiresAt = Date.now() + Math.max(3000, (38 - age) * 1000);
        }
        setCall({
          call_id: active.id,
          conversation_id: active.conversation_id,
          peer_name: active.caller_name,
          media: active.media || 'voice',
          role: 'callee',
          status: 'ringing',
          incoming_expires_at: expiresAt,
        });
        startIncomingRing();
      }
    } catch (e) { /* transient network — next resume retries */ }
  }, [api, isAuthenticated, user?.id, startIncomingRing]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return undefined;
    recoverActiveCall();
    const onVisible = () => { if (!document.hidden) recoverActiveCall(); };
    const onSwMessage = (event) => {
      const d = event.data;
      if (d?.type === 'PUSH_NOTIFICATION_CLICK' && (d.data?.kind === 'incoming_call' || d.data?.call_id)) {
        recoverActiveCall();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', recoverActiveCall);
    navigator.serviceWorker?.addEventListener('message', onSwMessage);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', recoverActiveCall);
      navigator.serviceWorker?.removeEventListener('message', onSwMessage);
    };
  }, [isAuthenticated, user?.id, recoverActiveCall]);

  // ── Server-truth reconciliation poller ──
  // WS delivery can be lost (dead/stale socket, device resume, proxy hiccup).
  // While a call is in progress, poll the authoritative state + missed signals
  // so the call still connects and ends correctly even without WS events.
  useEffect(() => {
    if (!call?.call_id) return undefined;
    const intervalMs = call.status === 'active' ? 6000 : 2500;
    let stopped = false;
    const tick = async () => {
      const current = callRef.current;
      if (stopped || !current) return;
      try {
        const { data } = await api.get(`/calls/${current.call_id}/state`, { params: { after: pollCursorRef.current } });
        if (stopped || callRef.current?.call_id !== current.call_id) return;
        if (data.status === 'ended') {
          const reason = data.end_reason;
          if (reason === 'declined' && current.role === 'caller') toast.info(`${current.peer_name} declined the call`);
          else if (reason === 'timeout' && current.role === 'caller') toast.info('Call not answered');
          else if (reason === 'cancelled' && current.role === 'callee') toast.info('Call cancelled');
          cleanup();
          return;
        }
        if (data.status === 'active' && current.status === 'ringing') {
          if (current.role === 'caller') {
            beginCallerOffer(); // missed call_accepted
          } else if (!answeringRef.current) {
            cleanup(); // answered on my other device
            return;
          }
        }
        for (const sig of data.signals || []) {
          if (typeof sig.seq === 'number') pollCursorRef.current = Math.max(pollCursorRef.current, sig.seq);
          await processSignal(sig);
        }
      } catch (e) {
        if (e?.response?.status === 404) cleanup();
      }
    };
    const iv = setInterval(tick, intervalMs);
    return () => { stopped = true; clearInterval(iv); };
  }, [call?.call_id, call?.status, api, cleanup, beginCallerOffer, processSignal]);

  // Fail fast instead of hanging forever in "Connecting…" (e.g. NAT that
  // requires the TURN relay). 25s covers slow mobile ICE gathering.
  useEffect(() => {
    if (call?.status !== 'connecting') return undefined;
    const timer = setTimeout(() => {
      toast.error("Couldn't connect the call — this network may require the call relay server");
      endCall('connect_timeout');
    }, 25000);
    return () => clearTimeout(timer);
  }, [call?.status, endCall]);

  // Media watchdog — catches "connected but black/silent" calls (ICE picked a
  // pair that passes checks but drops RTP). No inbound bytes ~8s after connect
  // → automatic ICE restart; still nothing at ~24s → end with a clear error.
  useEffect(() => {
    if (call?.status !== 'active') return undefined;
    let cancelled = false;
    let restartTried = false;
    const startedAt = Date.now();
    const iv = setInterval(async () => {
      const pc = pcRef.current;
      if (!pc || cancelled) return;
      let bytes = 0;
      try {
        const stats = await pc.getStats();
        stats.forEach((r) => { if (r.type === 'inbound-rtp') bytes += (r.bytesReceived || 0); });
      } catch (e) { return; }
      if (bytes > 0) { clearInterval(iv); return; }
      const elapsed = Date.now() - startedAt;
      if (elapsed > 8000 && !restartTried) {
        restartTried = true;
        performIceRestartRef.current?.();
      } else if (elapsed > 24000) {
        clearInterval(iv);
        toast.error("No audio/video could get through on this network — the call was ended");
        endCall('connect_timeout');
      }
    }, 3000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [call?.status, endCall]);

  // Tell the peer when THIS side gets interrupted (backgrounded for a native
  // phone call / app switch, or the OS mutes our mic — iOS call interruption).
  useEffect(() => {
    if (!call || (call.status !== 'active' && call.status !== 'connecting')) return undefined;
    const callId = call.call_id;
    const notify = (kind) => sendSignal(callId, kind, {});
    const onVisibility = () => {
      // While screen-sharing, switching windows/apps is EXPECTED — never tell the
      // peer we're "away" (it would blur the very screen we're sharing).
      if (document.hidden && screenSharingRef.current) return;
      notify(document.hidden ? 'hold' : 'resume');
    };
    document.addEventListener('visibilitychange', onVisibility);
    const audio = localStreamRef.current?.getAudioTracks()[0];
    const onMute = () => notify('hold');
    const onUnmute = () => notify('resume');
    if (audio) {
      audio.addEventListener('mute', onMute);
      audio.addEventListener('unmute', onUnmute);
    }
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (audio) {
        audio.removeEventListener('mute', onMute);
        audio.removeEventListener('unmute', onUnmute);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.status, call?.call_id, sendSignal]);

  // Call quality sampler — small signal badge while on an active call
  const [callQuality, setCallQuality] = useState(null); // 'good' | 'fair' | 'poor'
  const [encrypted, setEncrypted] = useState(false); // verified live from DTLS transport state
  const qualityPrevRef = useRef(null);
  useEffect(() => {
    if (call?.status !== 'active') { setCallQuality(null); setEncrypted(false); qualityPrevRef.current = null; return undefined; }
    const iv = setInterval(async () => {
      const pc = pcRef.current;
      if (!pc) return;
      try {
        const stats = await pc.getStats();
        let rtt = null; let lost = 0; let received = 0; let dtlsOk = false;
        stats.forEach((r) => {
          if (r.type === 'candidate-pair' && r.nominated && r.state === 'succeeded' && typeof r.currentRoundTripTime === 'number') rtt = r.currentRoundTripTime;
          if (r.type === 'inbound-rtp') { lost += r.packetsLost || 0; received += r.packetsReceived || 0; }
          if (r.type === 'transport' && r.dtlsState === 'connected') dtlsOk = true;
        });
        setEncrypted(dtlsOk);
        const prev = qualityPrevRef.current;
        qualityPrevRef.current = { lost, received };
        let lossRate = 0;
        if (prev) {
          const dLost = Math.max(0, lost - prev.lost);
          const dRecv = Math.max(0, received - prev.received);
          lossRate = dLost + dRecv > 0 ? dLost / (dLost + dRecv) : 0;
        }
        let q = 'good';
        if ((rtt !== null && rtt > 0.5) || lossRate > 0.08) q = 'poor';
        else if ((rtt !== null && rtt > 0.25) || lossRate > 0.03) q = 'fair';
        setCallQuality(q);
      } catch (e) { /* stats unavailable — keep last value */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [call?.status]);

  return (
    <CallContext.Provider value={{
      call, muted, cameraOn, screenSharing, remoteHasVideo, callSeconds, callQuality, peerPaused, encrypted,
      volumeBoost, toggleVolumeBoost, canBoostVolume: !IS_IOS,
      startCall, acceptCall, declineCall, endCall, toggleMute, toggleCamera, toggleScreenShare, flipCamera, cameraFacing,
      canFlipCamera: multipleCameras,
      localStreamRef, remoteStreamRef,
      callChatToast, callChatUnread,
      clearCallChatUnread: () => {
        setCallChatUnread(0);
        setCallChatToast(null);
        if (chatToastTimerRef.current) { clearTimeout(chatToastTimerRef.current); chatToastTimerRef.current = null; }
      },
    }}>
      {children}
      {/* Persistent, DOM-attached sink for remote call audio (iOS Safari safe) */}
      <audio ref={remoteAudioElRef} autoPlay playsInline style={{ display: 'none' }} data-testid="call-remote-audio" />
    </CallContext.Provider>
  );
};
